/**
 * Employee/person avatar: shows the profile photo when available, falling
 * back to initials. Used in the roster table's name cells, the dashboard
 * cards, and anywhere a person needs a circle.
 *
 *     <Avatar name={e.full_name} photo={e.photo} size={32} />
 *
 * `photo` accepts the backend payload ({ master, thumb_64/128/256 }) or a
 * bare URL string (pre-migration rows) or null. The component picks the
 * smallest thumbnail that covers `size`, and degrades thumbnail → master
 * → initials on load error so a broken image never leaves an empty hole.
 *
 * When the avatar has a real photo it's clickable: it floats a larger
 * version (the master image) in a lightbox so HR can eyeball the face as
 * a verification image. Pass `previewable={false}` to disable.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

export type AvatarPhoto =
  | {
      master: string;
      thumb_64?: string;
      thumb_128?: string;
      thumb_256?: string;
    }
  | string
  | null
  | undefined;

export type AvatarTone = "slate" | "blue" | "purple";

const TONE_CLASS: Record<AvatarTone, string> = {
  slate: "bg-slate-soft text-navy",
  blue: "bg-gradient-to-br from-blue/15 to-cyan/15 text-blue",
  purple: "bg-gradient-to-br from-purple/15 to-blue/15 text-purple",
};

function resolvePhotoUrl(
  photo: AvatarPhoto,
  size: number,
  forceMaster = false,
): string | null {
  if (!photo) return null;
  if (typeof photo === "string") return photo;
  if (forceMaster) return photo.master;
  if (size <= 64) return photo.thumb_64 || photo.master;
  if (size <= 128) return photo.thumb_128 || photo.master;
  return photo.thumb_256 || photo.master;
}

export function initialsFromName(
  name?: string | null,
  fallback?: string | null,
): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return (fallback || "?").slice(0, 2).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface AvatarProps {
  name?: string | null;
  photo?: AvatarPhoto;
  /** Shown when there's no name (e.g. employee_id). */
  fallback?: string | null;
  /** Circle diameter in px. Default 32. */
  size?: number;
  tone?: AvatarTone;
  /** Click the photo to float a larger verification image. Default true. */
  previewable?: boolean;
  className?: string;
}

export function Avatar({
  name,
  photo,
  fallback,
  size = 32,
  tone = "slate",
  previewable = true,
  className,
}: AvatarProps) {
  const [src, setSrc] = useState<string | null>(() =>
    resolvePhotoUrl(photo, size),
  );
  const [previewOpen, setPreviewOpen] = useState(false);

  // Re-resolve when the photo prop changes (a reused row instance, a
  // freshly approved photo, …).
  useEffect(() => {
    setSrc(resolvePhotoUrl(photo, size));
  }, [photo, size]);

  const dims = { width: size, height: size };

  if (src) {
    const img = (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        style={dims}
        className={cn("h-full w-full rounded-full object-cover", className)}
        onError={() => {
          // thumbnail 404 → try master, then give up to initials.
          const master = resolvePhotoUrl(photo, size, true);
          setSrc(master && master !== src ? master : null);
        }}
      />
    );

    if (!previewable) {
      return (
        <span className="inline-block shrink-0" style={dims}>
          {img}
        </span>
      );
    }

    return (
      <>
        <button
          type="button"
          style={dims}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setPreviewOpen(true);
          }}
          aria-label={name ? `View photo of ${name}` : "View photo"}
          title="View photo"
          className="shrink-0 cursor-zoom-in rounded-full transition hover:ring-2 hover:ring-blue/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/60"
        >
          {img}
        </button>
        {previewOpen && (
          <PhotoPreview
            url={resolvePhotoUrl(photo, 256, true) ?? src}
            name={name}
            onClose={() => setPreviewOpen(false)}
          />
        )}
      </>
    );
  }

  const fontSize = size <= 28 ? 10 : size <= 36 ? 11 : size <= 44 ? 13 : 16;
  return (
    <span
      aria-hidden="true"
      style={{ ...dims, fontSize }}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-semibold",
        TONE_CLASS[tone],
        className,
      )}
    >
      {initialsFromName(name, fallback)}
    </span>
  );
}

/** Centered lightbox floating a regular-size copy of the photo. */
function PhotoPreview({
  url,
  name,
  onClose,
}: {
  url: string;
  name?: string | null;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={name ? `Photo of ${name}` : "Photo"}
      onClick={onClose}
      className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[80vh] w-auto max-w-xs flex-col overflow-hidden rounded-xl bg-white p-2 shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close photo"
          className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-black/40 text-white transition hover:bg-black/60"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
        <img
          src={url}
          alt={name ? `Photo of ${name}` : "Photo"}
          className="max-h-[70vh] w-72 max-w-full rounded-lg object-contain"
        />
        {name && (
          <p className="px-1 py-2 text-center text-sm font-medium text-navy">
            {name}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
